import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import axiosInstance from '../../../lib/axios';
import { toast } from 'react-toastify';
import { toCanvas } from 'qrcode';

const PrintAdvice = ({ contentRef, formData, PytAdvId }) => {
  const [adviceData, setAdviceData] = useState([]);
  const [printApiData, setPrintApiData] = useState(null);
  const [loadingPrintData, setLoadingPrintData] = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    if (moment(date, moment.ISO_8601, true).isValid()) {
      return moment(date).format('DD-MM-YYYY');
    }
    if (moment(date, 'DD/MM/YYYY', true).isValid()) {
      return moment(date, 'DD/MM/YYYY').format('DD-MM-YYYY');
    }
    if (moment(date, 'YYYY-MM-DD', true).isValid()) {
      return moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
    }
    return '';
  };

  const inr = (n) => {
    if (n == null || n === '') return '0.00';
    const num = Number(n) || 0;
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const monthIndex = (m) => {
    if (m == null) return null;
    const map = {
      jan: 1, january: 1,
      feb: 2, february: 2,
      mar: 3, march: 3,
      apr: 4, april: 4,
      may: 5,
      jun: 6, june: 6,
      jul: 7, july: 7,
      aug: 8, august: 8,
      sep: 9, sept: 9, september: 9,
      oct: 10, october: 10,
      nov: 11, november: 11,
      dec: 12, december: 12,
    };
    const s = String(m).trim().toLowerCase();
    if (map[s]) return map[s];
    const n = Number(s);
    if (!Number.isNaN(n) && n >= 1 && n <= 12) return n;
    return null;
  };

  const monthShort = (idx) => ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][idx] || '';

  const txSummary = useMemo(() => {
    if (!adviceData?.length) return null;

    const normalized = adviceData.map(r => {
      let y = r?.YearNm ? Number(r.YearNm) : null;
      let m = r?.MonthNm != null ? monthIndex(r.MonthNm) : null;

      if ((!y || !m) && r?.BookDt) {
        const d = moment(r.BookDt);
        if (d.isValid()) {
          y = y || d.year();
          m = m || d.month() + 1;
        }
      }
      return {
        dptType: r?.DptType || 'Rent',
        year: y,
        month: m,
        bookDate: r?.BookDt ?? null,
      };
    }).filter(x => x.year && x.month);

    if (!normalized.length) return null;

    const sorted = normalized.slice().sort((a, b) => (a.year - b.year) || (a.month - b.month));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    let monthRangeLabel = '';
    if (first.year === last.year) {
      monthRangeLabel = first.month === last.month
        ? `${monthShort(first.month)} ${first.year}`
        : `${monthShort(first.month)}–${monthShort(last.month)} ${first.year}`;
    } else {
      monthRangeLabel = `${monthShort(first.month)} ${first.year}–${monthShort(last.month)} ${last.year}`;
    }

    const validDates = normalized
      .map(x => x.bookDate)
      .filter(Boolean)
      .map(d => moment(d))
      .filter(d => d.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());
    let bookDateLabel = '-';
    if (validDates.length) {
      const start = validDates[0];
      const end = validDates[validDates.length - 1];
      bookDateLabel = start.isSame(end, 'day')
        ? start.format('DD-MM-YYYY')
        : `${start.format('DD-MM-YYYY')} – ${end.format('DD-MM-YYYY')}`;
    }

    const dptTypes = Array.from(new Set(normalized.map(n => n.dptType)));
    const dptLabel = dptTypes.length === 1 ? dptTypes[0] : 'Multiple';

    return {
      dptType: dptLabel,
      monthRangeLabel,
      bookDateLabel,
    };
  }, [adviceData]);

  const currentDateTime = moment().format('DD-MM-YYYY HH:mm:ss');

 
  useEffect(() => {
    if (PytAdvId && formData?.BookId && Number(formData.BookId) !== 0) {
      fetchMoreData({ PytAdvId, BookId: formData.BookId });
    }

  }, [PytAdvId, formData?.BookId]);

  const fetchMoreData = async (id) => {
    try {
      const response = await axiosInstance.post('TrnBooking/RetriveTrnPytAdv', {
        PytAdvId: parseInt(id.PytAdvId) || 0,
        BookId: parseInt(id.BookId) || 0,
        flag: 'PvtData',
      });
      if (response.data.Status === 0 && response.data.responseStatusCode === 1 && response.data.Data?.length > 0) {
        setAdviceData(response.data.Data);
      } else {
        setAdviceData([]);
      }
    } catch (error) {
      toast.error('Error while fetching the data.');
    }
  };

  const fetchPrintApiData = async () => {
    if (!PytAdvId) return;
    
    try {
      setLoadingPrintData(true);
      const payload = {
        "BookDtFrom": moment().subtract(1, 'year').format("YYYY-MM-DD"),
        "BookUpToDt": moment().format("YYYY-MM-DD"),
        "PropId": 1,
        "ResidentId": "",
        "RoomTypeId": "",
        "RoomViewId": "",
        "BookSt": PytAdvId.toString(),
        "Flag": "PyAdvPyMonthWise",
        "Id": 0
      };

      console.log("Print API Payload:", payload);

      const response = await axiosInstance.post("BookingReports/GetBookingReportsDetail", payload);
      
      if (response.data.Status === 0 && response.data.Data && response.data.Data.length > 0) {
        setPrintApiData(response.data.Data[0]);
        console.log("Print API Response:", response.data.Data[0]);
      } else {
        setPrintApiData(null);
        console.log("No data found from print API");
      }
    } catch (error) {
      console.error("Error fetching print API data:", error);
      setPrintApiData(null);
    } finally {
      setLoadingPrintData(false);
    }
  };
 useEffect(() => {
    if (PytAdvId) {
      fetchPrintApiData();
    }
  }, [PytAdvId]);
  const sheet = {
    container: {
      padding: '16px',
      maxWidth: '900px',
      margin: '0 auto',
      background: '#fff',
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      position: 'relative',
      color: '#1f2937',
    },
    headerBand: {
      background: 'linear-gradient(135deg, #635bff, #7c8aff)',
      borderRadius: '20px',
      padding: '16px 24px',
      color: '#fff',
      boxShadow: '0 6px 24px rgba(99,91,255,0.25)',
      marginBottom: '18px',
    },
    brandRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
    },
    brandName: { fontSize: '22px', fontWeight: 700, letterSpacing: '0.2px' },
    badge: {
      display: 'inline-block',
      padding: '5px 10px',
      fontSize: '12px',
      borderRadius: '999px',
      background: 'rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.35)',
      backdropFilter: 'blur(4px)',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      marginTop: '10px',
    },
    card: {
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(17,24,39,0.06)',
      marginBottom: '14px',
    },
    cardHeader: {
      background: '#f3f4f6',
      padding: '8px 14px',
      fontWeight: 700,
      fontSize: '16px',
      borderBottom: '1px solid #e5e7eb',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    th: {
      padding: '10px 12px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f9fafb',
      textAlign: 'left',
      fontSize: '14px',
      fontWeight: 600,
    },
    td: {
      padding: '10px 12px',
      borderBottom: '1px solid #f1f5f9',
      fontSize: '14px',
    },
    label: { fontWeight: 600, color: '#374151', width: '28%' },
    value: { color: '#111827' },
    chip: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '999px',
      background: '#eef2ff',
      border: '1px solid #e0e7ff',
      fontSize: '12px',
      fontWeight: 600,
      color: '#3730a3',
    },
    moneyRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', padding: '12px 14px' },
    moneyCell: {
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '10px 12px',
      background: '#fafafa',
    },
    moneyLabel: { fontSize: '12px', color: '#6b7280' },
    moneyValue: { fontSize: '16px', fontWeight: 700 },
    footer: {
      marginTop: '16px',
      fontSize: '13px',
      color: '#4b5563',
      borderTop: '1px dashed #e5e7eb',
      paddingTop: '12px',
    },
    topRightDate: {
      position: 'absolute',
      top: '10px',
      right: '20px',
      fontSize: '12px',
      color: '#374151',
    },
      qrContainer: {
    textAlign: 'center',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    background: '#fafafa',
  },
  qrLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '8px',
    fontWeight: 600,
  },

  };
  useEffect(() => {
  const generateQRCode = async () => {
    try {
      const canvas = document.getElementById('qr-code');
      if (canvas) {
        await toCanvas(canvas, 'https://www.clanbridge.com/', {
          width: 120,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      }
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  generateQRCode();
}, []);

  return (
    <div ref={contentRef} style={sheet.container}>
      <h2 style={{textAlign:'center'}}>Payment Advice/Invoice</h2>
      <div style={sheet.topRightDate}>{currentDateTime}</div>
      <div style={sheet.headerBand}>
        <div style={sheet.brandRow}>
          <div style={sheet.brandName}>{formData.PropName || 'Property'}</div>
          <span style={sheet.badge}>Payment Advice</span>
        </div>
        <div style={sheet.infoGrid}>
          <div><span style={{ opacity: 0.9 }}>Advice No:&nbsp;</span><span style={{ fontWeight: 700 }}>{formData.PytAdvNo || '-'}</span></div>
          <div><span style={{ opacity: 0.9 }}>Advice Date:&nbsp;</span><span style={{ fontWeight: 700 }}>{formatDate(formData.RecDt) || '-'}</span></div>
          <div><span style={{ opacity: 0.9 }}>Book No:&nbsp;</span><span style={{ fontWeight: 700 }}>{formData.BookNo || '-'}</span></div>
          <div><span style={{ opacity: 0.9 }}>Book Date:&nbsp;</span><span style={{ fontWeight: 700 }}>{formatDate(formData.BookDate) || '-'}</span></div>
        </div>
      </div>

      <div style={sheet.card}>
        <div style={sheet.cardHeader}>Advice Details</div>
        <table style={sheet.table}>
          <tbody>
            <tr>
              <td style={{ ...sheet.td, ...sheet.label }}>Property Name</td>
              <td style={{ ...sheet.td, ...sheet.value }}>
                {formData.PropName || '-'}
              </td>
              <td style={{ ...sheet.td, ...sheet.label }}>Resident Name</td>
              <td style={{ ...sheet.td, ...sheet.value }}>
                {formData.ResidentName || '-'}
              </td>
            </tr>
            <tr>
              <td style={{ ...sheet.td, ...sheet.label }}>Mobile No</td>
              <td style={{ ...sheet.td, ...sheet.value }}>{formData.MobileNo || '-'}</td>
              <td style={{ ...sheet.td, ...sheet.label }}>Email</td>
              <td style={{ ...sheet.td, ...sheet.value }}>{formData.Email || '-'}</td>
            </tr>
            <tr>
              <td style={{ ...sheet.td, ...sheet.label }}>Remark</td>
              <td style={{ ...sheet.td, ...sheet.value }}>
                <span style={sheet.chip}>{formData.Remark || '—'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!!adviceData.length && (
        <div style={sheet.card}>
          <div style={sheet.cardHeader}>Transaction Details</div>
          <table style={sheet.table}>
            <thead>
              <tr>
                <th style={sheet.th}>Deposit Type</th>
                <th style={sheet.th}>Month Range</th>
                <th style={sheet.th}>Book Date(s)</th>
                <th style={sheet.th}>Advice No</th>
                <th style={sheet.th}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={sheet.td}>{txSummary?.dptType || 'Rent'}</td>
                <td style={sheet.td}>
                  <span style={sheet.chip}>{txSummary?.monthRangeLabel || '-'}</span>
                </td>
                <td style={sheet.td}>{txSummary?.bookDateLabel || '-'}</td>
                <td style={sheet.td}>{formData.PytAdvNo || '-'}</td>
                <td style={sheet.td}>{formData.Amount || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
   {loadingPrintData ? (
        <div style={sheet.card}>
          <div style={sheet.cardHeader}>Payment Schedule Details</div>
          <div style={sheet.loadingText}>Loading payment details...</div>
        </div>
      ) : printApiData ? (
        <div style={sheet.card}>
          <div style={sheet.cardHeader}>Payment Schedule Details </div>
          <table style={sheet.table}>
            <tbody>
              <tr>
                <td style={{ ...sheet.td, ...sheet.label }}>Payment Advice Date</td>
                <td style={sheet.td}>{formatDate(printApiData.PytAdvDt)}</td>
                <td style={{ ...sheet.td, ...sheet.label }}>Installment No</td>
                <td style={sheet.td}>{printApiData.InstallmentNo || '-'}</td>
              </tr>
              <tr>
                <td style={{ ...sheet.td, ...sheet.label }}>Booking Date</td>
                <td style={sheet.td}>{formatDate(printApiData.BookDt)}</td>
                <td style={{ ...sheet.td, ...sheet.label }}>Amount</td>
                <td style={sheet.td}>₹{inr(printApiData.Amount)}</td>
              </tr>
              
               {printApiData.Strwhere && (
          <>
            <tr>
              <td style={{ ...sheet.td, ...sheet.label }}>Payment Tenure</td>
              <td style={sheet.td} colSpan="3">
                {printApiData.Strwhere.split(') , ')[0] + ')' || '-'}
              </td>
            </tr>
            <tr>
              <td style={{ ...sheet.td, ...sheet.label }}>Payment Months</td>
              <td style={sheet.td} colSpan="3">
                {printApiData.Strwhere.split(') , ')[1] || '-'}
              </td>
            </tr>
          </>
        )}
            </tbody>
          </table>
        </div>
      ) : null}
      <div style={sheet.footer}>
        <p style={{ margin: 0 }}>Thank you for choosing Clanbridge Living Pvt Ltd.</p>
        <p style={{ margin: 0 }}>For any queries, please contact: support@clanbridgeliving.in</p>
      </div>
      <div style={sheet.qrContainer}>
  <div style={sheet.qrLabel}>Scan to visit our website</div>
  <canvas id="qr-code"></canvas>
</div>
    </div>
  );
};

export default PrintAdvice;
